// A goal: how far along it is, when it ends and whether it will get there.
// A goal reads one number. A level (MRR, subscribers, a balance) counts from
// where it started; a total over a period (orders this month, sessions in the
// last 28 days) counts from zero. It ends with its calendar period (orders
// this month end with the month) or on the date it was given, and its pace is
// said only from what it has read: never a guess dressed as a number.

import { formatNumber } from "./format";
import { calendarSpan, DAY, isCalendar } from "./period";
import type { NumberFormat, Point, Stream } from "./types";

export interface GoalView {
  /** the number now, or null when there is none */
  value: number | null;
  target: number;
  start: number;
  /** how far from start to target, 0 to 1 and beyond */
  pct: number;
  done: boolean;
  /** a total over a period, which counts from zero; else a level */
  total: boolean;
  /** the time it ends (the start of the day after its last), when it has one */
  ends: number | null;
  /** "16 days left", "Last day", "Ended Oct 31" */
  left: string | null;
  /** whether it will get there: said only from what was read */
  pace: { text: string; tone: "good" | "warn" | null } | null;
}

/** A date someone set as a goal's end, "2026-12-31": the start of the day after it, in the viewer's time, or null. */
export function goalBy(v: unknown): number | null {
  if (typeof v !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const t = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1).getTime();
  return Number.isFinite(t) ? t : null;
}

const dateWords = (t: number, now: number) =>
  new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", year: new Date(t).getFullYear() !== new Date(now).getFullYear() ? "numeric" : undefined });

/** A rate a day, rounded so it reads: "4.2", "$310". */
function perDay(v: number, f: NumberFormat | null | undefined): string {
  if (f?.style === "currency") return formatNumber(Math.ceil(v), f);
  return formatNumber(v < 10 ? Math.ceil(v * 10) / 10 : Math.ceil(v), f && f.style !== "percent" ? { ...f, decimals: v < 10 ? undefined : 0 } : null);
}

/** When a level reaches its target at the pace of its last 30 days, a straight line through them; only when it is going the right way, and within three years. */
export function projection(points: Point[], target: number): number | null {
  if (points.length < 4) return null;
  const last = points[points.length - 1];
  const win = points.filter((p) => p.t >= last.t - 30 * DAY);
  if (win.length < 4 || last.t - win[0].t < 7 * DAY) return null;
  const n = win.length;
  const mx = win.reduce((a, p) => a + p.t, 0) / n;
  const my = win.reduce((a, p) => a + p.v, 0) / n;
  let num = 0;
  let den = 0;
  for (const p of win) {
    num += (p.t - mx) * (p.v - my);
    den += (p.t - mx) ** 2;
  }
  const slope = den ? num / den : 0;
  if (slope <= 0 || last.v >= target) return null;
  const t = last.t + (target - last.v) / slope;
  return t - last.t < 3 * 365 * DAY ? t : null;
}

/** Everything a goal says, from its settings and the stream it reads. */
export function goalView(props: Record<string, unknown>, s: Pick<Stream, "value" | "points" | "period" | "format">, now = Date.now()): GoalView {
  const target = Number(props.target ?? 0) || 0;
  const total = !!s.period;
  // a total counts from zero: what it started from is a level's
  const start = total ? 0 : Number(props.start ?? 0) || 0;
  const value = typeof s.value === "number" && Number.isFinite(s.value) ? s.value : null;
  const pct = value !== null && target > start ? (value - start) / (target - start) : 0;
  const done = value !== null && target > 0 && value >= target;
  // a calendar period ends with itself (the one its data is of, which a read just after it ends still shows); anything else on the date it was given
  const cal = s.period && isCalendar(s.period.days) ? calendarSpan(s.period.days, s.period.from) : null;
  const ends = cal ? cal.end : goalBy(props.by);
  const daysLeft = ends !== null ? Math.ceil((ends - now) / DAY) : null;
  const left = daysLeft === null ? null : daysLeft > 1 ? `${daysLeft} days left` : daysLeft === 1 ? "Last day" : `Ended ${dateWords(ends! - DAY, now)}`;
  let pace: GoalView["pace"] = null;
  if (done) pace = { text: "Reached. Set the next one.", tone: "good" };
  else if (value === null) pace = null;
  else if (cal && daysLeft !== null && daysLeft > 0) {
    // so far this month: at the rate of the days gone, where it ends up; said once a tenth of it, or three days, has gone
    const gone = now - cal.from;
    const span = cal.end - cal.from;
    if (gone >= Math.min(3 * DAY, span * 0.1)) {
      const projected = value / (gone / span);
      pace = projected >= target ? { text: `On pace for ${formatNumber(Math.floor(projected), s.format)}`, tone: "good" } : { text: `Needs ${perDay((target - value) / daysLeft, s.format)} a day`, tone: "warn" };
    }
  } else if (!total) {
    const eta = projection(s.points, target);
    if (ends !== null && daysLeft !== null && daysLeft > 0) {
      if (eta !== null && eta <= ends) pace = { text: `On track for ${dateWords(eta, now)}`, tone: "good" };
      else if (eta !== null) pace = { text: `At this pace, ${dateWords(eta, now)}`, tone: "warn" };
      else pace = { text: `Needs ${perDay((target - value) / daysLeft, s.format)} more a day`, tone: "warn" };
    } else if (eta !== null) pace = { text: `At this pace, ${dateWords(eta, now)}`, tone: null };
  }
  // a total over the last n days rolls on with each day, so it has no pace toward a date
  return { value, target, start, pct, done, total, ends, left, pace };
}
